import { useEffect, useCallback } from "react";
import { useActivitiesStore } from "../stores/activitiesStore";

/**
 * Hook for managing locally stored activities.
 * Provides access to activities list, upload, sync, and delete functionality.
 */
export function useActivities(options?: { autoFetch?: boolean }) {
  const store = useActivitiesStore();

  // Auto-fetch on mount if requested
  useEffect(() => {
    if (options?.autoFetch && store.activities.length === 0 && !store.loading) {
      store.fetchActivities();
    }
  }, [options?.autoFetch]);

  return {
    // List state
    activities: store.activities,
    total: store.total,
    loading: store.loading,
    error: store.error,

    // Upload state
    uploading: store.uploading,
    uploadError: store.uploadError,
    uploadDuplicate: store.uploadDuplicate,

    // Sync state
    syncing: store.syncing,
    syncResult: store.syncResult,
    syncError: store.syncError,
    syncNeedsReconnect: store.syncNeedsReconnect,
    syncRetryAfter: store.syncRetryAfter,

    // Actions
    fetchActivities: store.fetchActivities,
    uploadFile: store.uploadFile,
    deleteActivity: store.deleteActivity,
    syncFromProvider: store.syncFromProvider,
    clearUploadState: store.clearUploadState,
    clearSyncState: store.clearSyncState,
  };
}

/**
 * Hook for fetching and managing a single activity.
 */
export function useActivity(activityId: string | undefined) {
  const store = useActivitiesStore();

  // Fetch activity on mount or when ID changes
  useEffect(() => {
    if (activityId && activityId !== store.currentActivity?.id) {
      store.fetchActivity(activityId);
    }
  }, [activityId]);

  // Find in list for navigation (prev/next)
  // Activities are sorted newest-first, so:
  // - "previous" (left chevron) = older activity = higher index
  // - "next" (right chevron) = newer activity = lower index
  const activities = store.activities;
  const currentIndex = activities.findIndex((a) => a.id === activityId);
  const previousActivity = currentIndex < activities.length - 1 ? activities[currentIndex + 1] : null;
  const nextActivity = currentIndex > 0 ? activities[currentIndex - 1] : null;

  const refresh = useCallback(() => {
    if (activityId) {
      store.fetchActivity(activityId);
    }
  }, [activityId]);

  return {
    activity: store.currentActivity,
    loading: store.currentActivityLoading,
    error: store.currentActivityError,

    // Navigation
    previousActivity,
    nextActivity,

    // Actions
    refresh,
    deleteActivity: store.deleteActivity,
  };
}
